/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import { ModalBg, PlusIco } from '@styles/base.style'
import { message, Popover, Radio } from 'antd'
import { Button, SwitchBtn } from '@styles/button.style'
import CloseBtnImg from '@assets/images/btn/close-btn.png'
import useRootStore from '@store/useRootStore'
import _ from 'lodash'
import { UserExtraOddsItem } from '@interface/common'
import { CLUB_MEMBERSHIP_ID } from 'adv-protocol/src/GameCommon'
import { DividendCardWrap } from '../index.style'
import AutoSelectionGuidePopup from './AutoSelectionGuidePopup'
import { Modal } from 'antd-mobile'
import {
  isAvailableGotBlessItemFoldCondition,
} from '@utils/utils'
import { GotBlessItemNotUsableInfo } from '@views/Matchup/BetSlip/AdditionalDividendCard/GotBlessItemNotUsableInfo'
import StorageUtils from '@utils/storage-utils'
import { AdvButton } from '@components/Button'

const AdditionalDividendCard: React.FC = () => {
  const { authStore, componentStore, matchupStore, globalStore } = useRootStore()
  const {
    extraOddsItemDetails,
    selectedUserExtraOddsItem,
    getUserExtraOddsItemCountByPropertyId,
    getUserExtraOddsItemTotalCount,
  } = componentStore
  const { club } = matchupStore

  const [visibleCardList, setVisibleCardList] = useState(false)
  const [autoSelect, setAutoSelect] = useState(false)
  const [visibleGuide, setVisibleGuide] = useState(false)

  useEffect(() => {
    setAutoSelect(componentStore.isGodBlessItemAutoSelectionEnabledByClubMembership())
  }, [club])

  if (_.isEmpty(extraOddsItemDetails)) {
    return <Fragment/>
  }

  const items = componentStore.makeUserExtraOddsItems()
  const betSlipPC = document.getElementById('pcBetslip')

  const onClickShowCardList = (): void => {
    if (_.isEmpty(matchupStore.cart)) {
      message.info('선택된 경기가 없습니다.')
      return
    }

    if (_.isEmpty(items)) {
      message.info('보유한 신의 가호가 없습니다.')
      return
    }

    if (!isAvailableGotBlessItemFoldCondition(matchupStore.cart)) {
      globalStore.pushDialogOk({
        custom: <GotBlessItemNotUsableInfo/>,
        className: 'box-type big-size character-popup',
      })
      return
    }

    if (!isMobile && globalStore.visibleMatchFilterPopup) {
      betSlipPC.classList.add('z-index-10001')
    }

    authStore.refreshSimpleUserInfo(false)
    setVisibleCardList(true)
  }

  const onCloseModal = () => {
    setVisibleCardList(false)

    if (!isMobile && globalStore.visibleMatchFilterPopup) {
      betSlipPC.classList.remove('z-index-10001')
    }
  }

  const onClickResetSelection = (): void => {
    componentStore.setSelectedUserExtraOddsItem(null)
  }

  let eleImg
  let eleName
  let eleRate

  if (!_.isEmpty(selectedUserExtraOddsItem)) {
    const {
      name,
      rate,
      imgBaseUrl,
      cardImgUrl,
    } = selectedUserExtraOddsItem

    eleImg = <img src={`${imgBaseUrl}${cardImgUrl}`} alt={`${name} 배당 ${rate}`} className="selected-img"/>
    eleName = name
    eleRate = rate
  }

  const popoverTxt = (
    <span className="popover-content-txt">
        선택한 신의 가호를 모두 사용하면<br/>
        <span style={{ color: '#26aab8' }}>가장 높은 배당의 신의 가호</span>로<br/>
        자동 선택됩니다.
      </span>
  )

  const popoverTrigger = isMobile ? 'click' : 'hover'

  const onClickSelectCard = (item: UserExtraOddsItem): void => {
    if (selectedUserExtraOddsItem?.propertyId === item.propertyId) {
      componentStore.setSelectedUserExtraOddsItem(null)
      return
    }
    componentStore.setSelectedUserExtraOddsItem(item)
  }

  const saveAutoSelectionChange = (checked: boolean): void => {
    const { club } = matchupStore
    if (club.grade === CLUB_MEMBERSHIP_ID.FREE) {
      StorageUtils.saveGodBlessItemAutoSelectionFreeClubEnabled(checked)
    } else {
      StorageUtils.saveGodBlessItemAutoSelectionMemberShipCLubEnabled(checked)
    }
  }

  const onAutoSelectionChange = (e): void => {
    const checked = e.currentTarget.checked
    setAutoSelect(checked)
    saveAutoSelectionChange(checked)

    if (!checked) {
      return
    }

    const showedAt = StorageUtils.getGodBlessItemAutoSelectionGuidePopupShowedAt()
    if (!showedAt) {
      setVisibleGuide(true)
    }

    if (!_.isEmpty(selectedUserExtraOddsItem)) {
      return
    }

    const newUserItems = componentStore.makeUserExtraOddsItems()
    if (_.isEmpty(newUserItems)) {
      componentStore.setSelectedUserExtraOddsItem(null)
      return
    }
    componentStore.setSelectedUserExtraOddsItem(newUserItems[0])
  }

  const selectCardPopup = (
    <Modal
      transparent
      visible={visibleCardList}
      maskClosable={false}
      onClose={onCloseModal}
      className="event"
    >
      <DividendCardWrap>
        <ModalBg/>

        <div className="contents-wrap">
          <div className="switch">
            <Popover
              placement="top"
              content={popoverTxt}
              trigger={popoverTrigger}
              className="popover"
              overlayClassName="white-popover only-txt"
              getPopupContainer={(triggerNode: any) => triggerNode.parentNode} /* body 하위에 div를 생성하지 않고 additional-dividend-mark과 동일 선상에 생성되도록 설정 - body 하위에 생성되면 아이폰에서 모달 뒤에 뜸 */
            >
              <div>
                자동 선택
              </div>
            </Popover>

            <SwitchBtn
              type="checkbox"
              id="automaticSelection"
              name="automaticSelection"
              className="auto-select"
              value="autoSelect"
              checked={autoSelect}
              onChange={onAutoSelectionChange}
              width={58}
              height={22}
              marginLeft={8}
            />
            <label htmlFor="automaticSelection" className="on-off-wrap">
              <span className="on">ON</span>
              <span className="off">OFF</span>
            </label>
          </div>

          <Radio.Group key="additionalDividendCardGroup" value={selectedUserExtraOddsItem?.rate}
                       className={selectedUserExtraOddsItem ? 'card-list selected' : 'card-list'}>
            {items.map(item =>
              <Radio.Button key={`AdditionalDividendCard:additionalDividendCardGroup:${item.key}`}
                            value={item.rate}
                            className="card-item" onClick={() => onClickSelectCard(item)}>
                <img src={`${item.imgBaseUrl}${item.betSlipCardImgUrl}`} alt={item.name}/>
                <span className="card-quantity">{getUserExtraOddsItemCountByPropertyId(item.propertyId)}개</span>
              </Radio.Button>,
            )}
          </Radio.Group>
          
          <AdvButton
            blue
            txtAlignCenter
            width={150}
            height={32}
            text={['확인']}
            borderRadius={16}
            onClick={onCloseModal}
          />
        </div>
      </DividendCardWrap>
    </Modal>
  )

  return (
    <Fragment>
      <div className="additional-dividend">
        <Button className="select-btn" onClick={onClickShowCardList}>
          {selectedUserExtraOddsItem ? eleImg : <PlusIco width={10} height={10} scale={1.5} color="#B7B7B7"/>}
        </Button>
        <div className="selected-info">
          <div className="point-color--blue">추가배당</div>
          <div className="selected-card">
            {selectedUserExtraOddsItem ? eleName : <span className="point-color--gray--modal">신의 가호 선택</span>}
            (보유{selectedUserExtraOddsItem
            ? getUserExtraOddsItemCountByPropertyId(selectedUserExtraOddsItem.propertyId)
            : getUserExtraOddsItemTotalCount()}개)
            {selectedUserExtraOddsItem &&
              <Button TP onClick={onClickResetSelection} className={!isMobile && 'pc-clear-btn'}>
                <img src={CloseBtnImg} alt="닫기"/>
              </Button>
            }
          </div>
        </div>
        <span className="dividend-value">
          &#43;  {selectedUserExtraOddsItem ? eleRate : '0.00'}
        </span>
      </div>
      {selectCardPopup}

      <AutoSelectionGuidePopup visible={visibleGuide} setVisible={setVisibleGuide}/>
    </Fragment>
  )
}

export default observer(AdditionalDividendCard)
